/**
 * Making a control read-only without taking it away.
 *
 * Hard invariant 4: never `disabled` for a field somebody else is holding, or
 * for a form showing a past version. `disabled` removes the control from the
 * tab order and from the accessibility tree, and blocks text selection — so a
 * screen-reader user loses the value entirely, and a sighted user cannot even
 * copy it. A locked field still has something to say; it just cannot be
 * changed.
 *
 * Lives here because two forms now need it: the full editor and the short
 * form on the wallets page, both of which can show a version from the
 * history.
 */

/** Real `readonly` on real inputs — tab order, a11y tree and text selection all stay intact. */
export function applyFieldReadOnly(el: Element | null, on: boolean): void {
  if (!el) return;
  el.toggleAttribute('readonly', on);
  el.setAttribute('aria-readonly', String(on));
}

/**
 * `<select>` has no native `readonly` — disabling it would violate invariant 4
 * just to fake one. Instead it stays a real, focusable, fully announced
 * control and only the interactions that could change its value are blocked:
 * opening the native picker (`mousedown`) and any key that isn't plain `Tab`
 * navigation. There is no submit path in read-only mode regardless, so this
 * is belt-and-braces, not the only thing preventing a write.
 */
export function applySelectReadOnly(el: HTMLSelectElement | null, on: boolean): void {
  if (!el) return;
  el.setAttribute('aria-readonly', String(on));
  el.onmousedown = on ? (e) => e.preventDefault() : null;
  el.onkeydown = on ? (e) => { if (e.key !== 'Tab') e.preventDefault(); } : null;
}

/**
 * The same treatment for a control that is a button: never `disabled`, so it
 * keeps its place in the tab order and its text can still be read and
 * selected — it simply does not open anything.
 */
export function applyButtonReadOnly(el: HTMLElement | null, on: boolean): void {
  if (!el) return;
  el.setAttribute('aria-readonly', String(on));
  el.onkeydown = on ? (e) => { if (e.key !== 'Tab') e.preventDefault(); } : null;
  // Deliberately NOT suppressing `click` here. An `onclick` property handler
  // cannot reliably pre-empt a listener added with `addEventListener` before
  // it, so it would look like a guard while being one only sometimes — worse
  // than no guard. A host that opens something from this button checks its
  // own read-only state at the point of opening.
}
